import { useState } from 'react';
import { emailRoute } from '../../../lib/funnelAuthClient';
import { useOAuthPopup } from '../useOAuthPopup';
import { FACEBOOK_LOGIN_BUTTON_ENABLED } from '../authProviders';
import { useAuthModal } from '../../../contexts/AuthModalContext';
import {
  socialButtonVariants,
  fieldInputVariants,
  primaryButtonVariants,
  Spinner,
  ErrorBanner,
  GoogleIcon,
  FacebookIcon,
} from '../authFormPrimitives';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Ported from roammyway_funnel's EmailStep.tsx -- same markup/copy, decoupled
// from useSignInLogic so it can run in Vite. Google/Facebook open the
// OAuth-only popup instead of next-auth/react's signIn().
export default function EmailStep({ initialEmail, onRouted }) {
  const { handleAuthenticated } = useAuthModal();
  const { openOAuthPopup } = useOAuthPopup();
  const [email, setEmail] = useState(initialEmail || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [facebookLoading, setFacebookLoading] = useState(false);

  const isAnyLoading = loading || googleLoading || facebookLoading;

  const startOAuth = (provider, setProviderLoading) => {
    setError(null);
    setProviderLoading(true);
    // The popup closing (or finishing) isn't proof of anything by itself --
    // it fires the same way whether sign-in succeeded, the user cancelled,
    // or the funnel rejected it. handleAuthenticated() independently
    // re-verifies the real session before deciding which happened.
    const { blocked } = openOAuthPopup(provider, async () => {
      setProviderLoading(false);
      const { ok } = await handleAuthenticated();
      if (!ok) {
        setError('Sign-in was not completed. Please try again.');
      }
    });
    if (blocked) {
      setProviderLoading(false);
      setError('Your browser blocked the sign-in popup. Please allow popups for this site and try again.');
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const trimmed = email.trim();
    if (!EMAIL_REGEX.test(trimmed)) {
      setError('Please enter a valid email address.');
      return;
    }

    setLoading(true);
    try {
      const { ok, status, data } = await emailRoute(trimmed);
      if (!ok) {
        setError(
          status === 429
            ? 'Too many attempts. Please wait a moment and try again.'
            : data.error || 'Something went wrong. Please try again.',
        );
        return;
      }

      const nextStep = decodeEmailRouteStep(data.token);
      if (!nextStep) {
        setError('Something went wrong. Please try again.');
        return;
      }

      onRouted(trimmed, nextStep);
    } catch {
      setError('Network error. Please check your internet connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <h1 className="text-left font-brand text-[26px] font-bold leading-[1.2] tracking-[-0.3px] text-brand-blue lg:text-center lg:text-[28px] lg:tracking-[-0.4px]">
        Welcome
      </h1>
      <p className="mt-2 text-left font-brand text-sm leading-normal text-brand-text-secondary lg:text-center">
        Sign in or create your account to continue
      </p>

      <div className="mt-6">
        <button
          type="button"
          onClick={() => startOAuth('google', setGoogleLoading)}
          disabled={isAnyLoading}
          className={socialButtonVariants({ tone: 'google' })}
        >
          {googleLoading ? (
            <span className="flex items-center justify-center">
              <Spinner className="-ml-1 mr-2 text-brand-blue" />
              Signing in...
            </span>
          ) : (
            <>
              <GoogleIcon className="h-5 w-5" />
              Continue with Google
            </>
          )}
        </button>
        {/* Not rendered (absent from the DOM) until the Facebook app is Live --
            see FACEBOOK_LOGIN_BUTTON_ENABLED. */}
        {FACEBOOK_LOGIN_BUTTON_ENABLED && (
          <button
            type="button"
            onClick={() => startOAuth('facebook', setFacebookLoading)}
            disabled={isAnyLoading}
            className={`mt-3 ${socialButtonVariants({ tone: 'facebook' })}`}
          >
            {facebookLoading ? (
              <span className="flex items-center justify-center">
                <Spinner className="-ml-1 mr-2 text-white" />
                Signing in...
              </span>
            ) : (
              <>
                <FacebookIcon className="h-5 w-5" />
                Continue with Facebook
              </>
            )}
          </button>
        )}
      </div>

      <div className="my-5 flex items-center gap-3">
        <div className="h-px flex-1 bg-brand-border" />
        <span className="font-brand text-xs text-brand-text-secondary">or</span>
        <div className="h-px flex-1 bg-brand-border" />
      </div>

      {error && (
        <div className="mb-4">
          <ErrorBanner message={error} />
        </div>
      )}

      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        <div className="space-y-1.5">
          <label htmlFor="auth-flow-email" className="font-brand text-[13px] font-semibold text-brand-blue">
            Email address
          </label>
          <input
            id="auth-flow-email"
            name="email"
            type="email"
            autoComplete="email"
            required
            disabled={isAnyLoading}
            aria-invalid={Boolean(error)}
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (error) setError(null);
            }}
            placeholder="Enter your email"
            className={fieldInputVariants({ invalid: Boolean(error), filled: Boolean(email) })}
          />
        </div>

        <button type="submit" disabled={isAnyLoading} className={primaryButtonVariants({ tone: 'idle' })}>
          {loading ? (
            <span className="flex items-center justify-center">
              <Spinner className="-ml-1 mr-2 text-white" />
              Continuing...
            </span>
          ) : (
            'Continue'
          )}
        </button>
      </form>
    </>
  );
}

function base64UrlDecode(input) {
  const base64 = input.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
  return atob(padded);
}

function decodeEmailRouteStep(token) {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = JSON.parse(base64UrlDecode(parts[1]));
    return payload.step === 'password' || payload.step === 'signup' ? payload.step : null;
  } catch {
    return null;
  }
}
